import { parseRelativeBound } from './dateBounds';

/**
 * A CLOCK IN A NAMED TIME ZONE — what "today" and "now" read in a collection's
 * zone, for the defaults a new item starts with (the owner's ruling B11,
 * 2026-10-01: the web uses the COLLECTION's time zone for "today", the way the
 * server does). The browser's own zone is the person's, and the server never
 * sees it: an import, a rule and a public form are dated in the collection's
 * zone (keepr-api utils/timezone.js resolveCollectionTimezone), so the form
 * must be too, or the same create stores two different days.
 *
 * Mirrors keepr-api utils/dateOptions.js relativeValue / clockValue and the
 * scheduler's calendarParts / wallTimeToUtc (utils/automations/schedule.js),
 * through Intl alone — dayjs carries no zone plugin here, and one small module
 * is cheaper than a dependency. Not vendored: the API's copy reads its own Date
 * arithmetic; these functions are pinned against the same cases (the API's
 * test/elementDefaults.test.js zone cases) in
 * src/utils/__tests__/zonedClock.test.js, and against the shared zone vectors
 * both repos run (src/utils/zone/vectors.json, vendored byte-identical from
 * keepr-api utils/zone/vectors.json; KPR-1485) in
 * src/utils/zone/__tests__/zone.vectors.test.js.
 */

const formatters = new Map();

function formatterFor(zone) {
	if (!formatters.has(zone)) {
		formatters.set(zone, new Intl.DateTimeFormat('en-US', {
			timeZone: zone, hour12: false,
			year: 'numeric', month: '2-digit', day: '2-digit',
			hour: '2-digit', minute: '2-digit', second: '2-digit',
		}));
	}
	return formatters.get(zone);
}

/** Does the runtime know this zone? */
export function isKnownZone(zone) {
	if (typeof zone !== 'string' || !zone) return false;
	try {
		formatterFor(zone);
		return true;
	} catch (e) {
		return false;
	}
}

/**
 * Are two names the same zone to this runtime? Compares what Intl resolves
 * each to (`US/Pacific` and `America/Los_Angeles` are one zone; ICU's
 * `Asia/Kolkata` and `Asia/Calcutta` too) — never the strings as written.
 * For a comparison only: what Intl resolves a name to is never stored
 * (knowledge reference node-timezone-data). False when either is unknown.
 */
export function sameZone(a, b) {
	if (!isKnownZone(a) || !isKnownZone(b)) return false;
	return formatterFor(a).resolvedOptions().timeZone === formatterFor(b).resolvedOptions().timeZone;
}

/** The zone's wall clock at `date`: { y, monthIndex, d, hh, mm, ss }. */
export function wallPartsIn(zone, date) {
	const parts = Object.fromEntries(formatterFor(zone).formatToParts(date).map(p => [p.type, p.value]));
	// Some engines spell midnight "24" under hour12: false.
	const hh = parts.hour === '24' ? 0 : Number(parts.hour);
	return { y: Number(parts.year), monthIndex: Number(parts.month) - 1, d: Number(parts.day), hh, mm: Number(parts.minute), ss: Number(parts.second) };
}

const pad = n => String(n).padStart(2, '0');

/** The zone's calendar day at `date`, as a `date` element stores it. */
export function dayIn(zone, date) {
	const { y, monthIndex, d } = wallPartsIn(zone, date);
	return `${String(y).padStart(4, '0')}-${pad(monthIndex + 1)}-${pad(d)}`;
}

/** The zone's wall clock at `date`, as a `time` element stores it: 'HH:mm'. */
export function clockIn(zone, date) {
	const { hh, mm } = wallPartsIn(zone, date);
	return `${pad(hh)}:${pad(mm)}`;
}

/** Minutes east of UTC for `zone` at the instant `ms`. */
function offsetMinutesAt(zone, ms) {
	const p = wallPartsIn(zone, new Date(ms));
	return Math.round((Date.UTC(p.y, p.monthIndex, p.d, p.hh, p.mm, p.ss) - ms) / 60000);
}

/**
 * The fixed point ran out of steps: either it settled on its very last step
 * (a real wall time, kept as is) or `target` lies in a gap and the guesses
 * alternate between the two offsets around it. Those two candidates bracket
 * the transition, so a whole-minute bisection between them finds it. Anything
 * else is answered as the fixed point left it. keepr-api schedule.js
 * resolveGap, step for step.
 */
function resolveGap(zone, target, guess) {
	const other = target - offsetMinutesAt(zone, guess) * 60000;
	if (other === guess) return guess;
	let lo = Math.min(guess, other);
	let hi = Math.max(guess, other);
	const before = offsetMinutesAt(zone, lo);
	// Only a spring-forward gap brackets this way: the clock jumps ahead, so
	// the later candidate carries the larger offset.
	if (!(offsetMinutesAt(zone, hi) > before)) return guess;
	while (hi - lo > 60000) {
		const mid = lo + Math.floor((hi - lo) / 120000) * 60000;
		if (offsetMinutesAt(zone, mid) === before) lo = mid;
		else hi = mid;
	}
	return hi;
}

/**
 * The instant at which the zone's wall clock reads y-m-d hh:mm.
 *
 * A wall time that does not exist (inside a spring-forward gap: 00:00 in
 * Beirut on 2026-03-29, 02:30 in Los Angeles on 2026-03-08) is the FIRST
 * INSTANT AFTER THE GAP, the transition itself, so a midnight that does not
 * exist is still the first instant of its own day (KPR-1485). A wall time
 * that exists, or that happens twice, is answered as it always was. The same
 * rule as keepr-api utils/automations/schedule.js wallTimeToUtc.
 */
export function wallTimeToUtc(zone, y, monthIndex, d, hh = 0, mm = 0) {
	const target = Date.UTC(y, monthIndex, d, hh, mm);
	let guess = target;
	for (let i = 0; i < 3; i++) {
		const next = target - offsetMinutesAt(zone, guess) * 60000;
		if (next === guess) return new Date(guess);
		guess = next;
	}
	return new Date(resolveGap(zone, target, guess));
}

/**
 * keepr-api's addCalendarUnits, on UTC fields: days, weeks, hours and minutes
 * move the instant; a month or a year that lands past the end of the target
 * month stops on its last day (January 31 + 1 month is February 28).
 */
export function addCalendarUnits(from, sign, amount, token) {
	const out = new Date(from.getTime());
	const n = sign * amount;
	if (token === 'd') out.setUTCDate(out.getUTCDate() + n);
	else if (token === 'w') out.setUTCDate(out.getUTCDate() + 7 * n);
	else if (token === 'h') out.setUTCHours(out.getUTCHours() + n);
	else if (token === 'm') out.setUTCMinutes(out.getUTCMinutes() + n);
	else if (token === 'mo' || token === 'y') {
		const months = n * (token === 'y' ? 12 : 1);
		const m = out.getUTCMonth() + months;
		const year = out.getUTCFullYear() + Math.floor(m / 12);
		const month = ((m % 12) + 12) % 12;
		const last = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
		out.setUTCFullYear(year, month, Math.min(out.getUTCDate(), last));
	}
	return out;
}

/**
 * A relative DEFAULT ('today', 'today+30d', 'now+2h') as the value a new item
 * stores, read in `zone` — keepr-api utils/dateOptions.js relativeValue:
 *   today±N   the zone's calendar day, moved; a `date` stores the day, a
 *             `date-time` the instant that day starts in the zone
 *   now±N     the instant, moved (a `date-time` only)
 * null for a value the grammar does not read.
 */
export function relativeDefaultValue(value, dataType, now, zone) {
	const parts = parseRelativeBound(value);
	if (!parts) return null;
	if (parts.anchor === 'now') {
		const moved = parts.amount ? addCalendarUnits(now, parts.sign, parts.amount, parts.token) : new Date(now.getTime());
		return moved.toISOString();
	}
	const { y, monthIndex, d } = wallPartsIn(zone, now);
	const day = new Date(Date.UTC(y, monthIndex, d));
	const moved = parts.amount ? addCalendarUnits(day, parts.sign, parts.amount, parts.token) : day;
	if (dataType === 'date') return moved.toISOString().slice(0, 10);
	return wallTimeToUtc(zone, moved.getUTCFullYear(), moved.getUTCMonth(), moved.getUTCDate(), 0, 0).toISOString();
}
